import { useCallback, useRef } from 'react'

interface UseLongPressOptions {
  onLongPress: () => void
  onClick?: () => void
  delay?: number
}

interface UseLongPressResult {
  onTouchStart: (e: React.TouchEvent) => void
  onTouchEnd: (e: React.TouchEvent) => void
  onTouchMove: (e: React.TouchEvent) => void
  onMouseDown: (e: React.MouseEvent) => void
  onMouseUp: (e: React.MouseEvent) => void
  onMouseLeave: (e: React.MouseEvent) => void
}

/**
 * Hook for detecting long press gestures on mobile and desktop
 * @param options.onLongPress - Callback fired after long press duration
 * @param options.onClick - Optional callback for regular clicks
 * @param options.delay - Long press duration in ms (default: 500)
 */
export function useLongPress({
  onLongPress,
  onClick,
  delay = 500,
}: UseLongPressOptions): UseLongPressResult {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isLongPressRef = useRef(false)
  const startPosRef = useRef<{ x: number; y: number } | null>(null)
  const hasMovedRef = useRef(false)

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(
    (x: number, y: number) => {
      isLongPressRef.current = false
      hasMovedRef.current = false
      startPosRef.current = { x, y }

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true
        onLongPress()
      }, delay)
    },
    [onLongPress, delay]
  )

  const end = useCallback(
    (shouldTriggerClick: boolean) => {
      clear()
      // Only trigger click if it wasn't a long press
      if (shouldTriggerClick && !isLongPressRef.current && !hasMovedRef.current && onClick) {
        onClick()
      }
      startPosRef.current = null
      hasMovedRef.current = false
    },
    [clear, onClick]
  )

  const move = useCallback(
    (x: number, y: number) => {
      // Cancel long press if moved more than 10px
      if (startPosRef.current) {
        const dx = Math.abs(x - startPosRef.current.x)
        const dy = Math.abs(y - startPosRef.current.y)
        if (dx > 10 || dy > 10) {
          hasMovedRef.current = true
          clear()
        }
      }
    },
    [clear]
  )

  // Touch handlers
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      start(touch.clientX, touch.clientY)
    },
    [start]
  )

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      // Prevent default to avoid click events after long press
      if (isLongPressRef.current) {
        e.preventDefault()
      }
      end(!isLongPressRef.current)
    },
    [end]
  )

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0]
      move(touch.clientX, touch.clientY)
    },
    [move]
  )

  // Mouse handlers (for desktop testing)
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      start(e.clientX, e.clientY)
    },
    [start]
  )

  const onMouseUp = useCallback(() => {
    end(!isLongPressRef.current)
  }, [end])

  const onMouseLeave = useCallback(() => {
    clear()
  }, [clear])

  return {
    onTouchStart,
    onTouchEnd,
    onTouchMove,
    onMouseDown,
    onMouseUp,
    onMouseLeave,
  }
}
